import { Outlet, RouteObject } from 'react-router-dom';
import middlewareConfig, { MiddlewareType } from '@/router/middlewareConfig.tsx';

type RouteObjectWithMiddleware = RouteObject & {
  middleware?: MiddlewareType[];
  children?: RouteObjectWithMiddleware[];
};

export function transformRouterConfig(routerConfig: RouteObjectWithMiddleware[]): RouteObject[] {
  return routerConfig.map((route) => transformSingleRouteConfig(route));
}

function transformSingleRouteConfig(inputRoute: RouteObjectWithMiddleware) {
  const { middleware, element, path, children } = inputRoute;
  const transformMiddlewareToNestedStructure = (
    middlewares: RouteObjectWithMiddleware['middleware'],
    children: RouteObjectWithMiddleware['children'],
    path: RouteObjectWithMiddleware['path'],
    lastElement: RouteObjectWithMiddleware['element'],
    lastPath: RouteObjectWithMiddleware['path']
  ): RouteObject['children'] => {
    // Recursively nest the middlewares into a structure
    if (!middlewares || middlewares.length === 0) {
      return [
        {
          path: lastPath,
          element: lastElement,
          children: children ? transformRouterConfig(children) : undefined,
        },
      ];
    }

    const middleware = middlewares[0]; // Take the first middleware
    const remainingMiddlewares = middlewares.slice(1); // Remaining middlewares

    const currentMiddleware = middlewareConfig.middlewaresMap[middleware]; // Convert middleware to component
    if (!currentMiddleware) {
      console.warn(`Middleware ${middleware} not found. Skipping...`);
    }
    // Return the nested structure for the current middleware
    return [
      {
        path: path,
        element: currentMiddleware ?? <Outlet />,
        children: transformMiddlewareToNestedStructure(remainingMiddlewares, children, path, lastElement, lastPath), // Recursively nest remaining middlewares
      },
    ];
  };

  const hasChildren = children && children.length > 0;
  const hasMiddleware = middleware && middleware.length > 0;
  const currentPath = hasMiddleware ? '/' : path;
  // Transform the root route
  return {
    path: currentPath,
    element: hasMiddleware || hasChildren ? <Outlet /> : element,
    children:
      hasMiddleware || hasChildren
        ? transformMiddlewareToNestedStructure(middleware, children, currentPath, element, path)
        : undefined,
  };
}
